import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/analytics";

interface ScreenTrackingOptions {
  eventName: string;
  screenName: string;
  properties?: Record<string, any>;
}

/**
 * Hook to automatically track screen views with React Router integration.
 * Captures screen_name, previous_screen, and entry_point.
 *
 * @param options - Event name, screen name, and optional properties
 *
 * @example
 * useScreenTracking({
 *   eventName: AUTH_EVENTS.LOGIN_SCREEN_VIEWED,
 *   screenName: "login",
 *   properties: { entry_point: "landing_page" }
 * });
 */
export const useScreenTracking = ({
  eventName,
  screenName,
  properties = {},
}: ScreenTrackingOptions) => {
  const location = useLocation();
  const previousScreenRef = useRef<string | null>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    // Track only once per mount
    if (hasTrackedRef.current) {
      return;
    }

    const previousScreen = previousScreenRef.current;
    const entryPoint = location.state?.from || "direct_navigation";

    trackEvent(eventName, {
      screen_name: screenName,
      previous_screen: previousScreen,
      entry_point: entryPoint,
      source: "web",
      ...properties,
    });

    hasTrackedRef.current = true;

    // Update previous screen for next navigation
    return () => {
      previousScreenRef.current = screenName;
    };
  }, [eventName, screenName, location.state?.from, properties]);
};
